import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const StoreContext = createContext(null);

import { BACKEND_URL } from '../config';
import { error as logError } from '../lib/logger';

const DEFAULT_SETTINGS = {
  store_name: 'TailorFlow',
  store_logo: null,
  primary_color: '#C2A878',
  button_color: '#C2A878',
  tagline: 'Virtual Fitting for Custom Clothiers',
  show_powered_by: true,
  login_bg_image: null,
};

function hexToHsl(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function applyThemeColor(hex) {
  if (!hex || hex.length < 7) return;
  const { h, s, l } = hexToHsl(hex);
  const root = document.documentElement;
  root.style.setProperty('--primary', `${h} ${s}% ${l}%`);
  root.style.setProperty('--accent', `${h} ${s}% ${l}%`);
  root.style.setProperty('--ring', `${h} ${s}% ${l}%`);
  root.style.setProperty('--chart-1', `${h} ${s}% ${l}%`);
  // Store the hex for inline styles
  root.style.setProperty('--accent-hex', hex);
  // Lighter version for hover
  root.style.setProperty('--accent-hover', `hsl(${h}, ${Math.min(s + 5, 100)}%, ${Math.min(l + 10, 90)}%)`);
}

export function StoreProvider({ children }) {
  const { user } = useAuth();
  const [storeSettings, setStoreSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  const fetchStoreSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/store/settings`, { withCredentials: true });
      setStoreSettings({ ...DEFAULT_SETTINGS, ...data });
      if (data.primary_color) applyThemeColor(data.primary_color);
      if (data.button_color) {
        document.documentElement.style.setProperty('--btn-hex', data.button_color);
      }
    } catch (err) {
      logError('Failed to fetch store settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.id) {
      fetchStoreSettings();
      // Also apply from user object immediately
      if (user.primary_color) applyThemeColor(user.primary_color);
    } else {
      applyThemeColor('#C2A878');
      setLoading(false);
    }
  }, [user, fetchStoreSettings]);

  const updateStoreSettings = useCallback(async (settings) => {
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/store/settings`, settings, { withCredentials: true });
      const merged = { ...DEFAULT_SETTINGS, ...data };
      setStoreSettings(merged);
      if (merged.primary_color) applyThemeColor(merged.primary_color);
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || e.message };
    }
  }, []);

  const value = useMemo(
    () => ({ storeSettings, loading, updateStoreSettings, fetchStoreSettings }),
    [storeSettings, loading, updateStoreSettings, fetchStoreSettings]
  );

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
}

export function useStore() {
  const context = useContext(StoreContext);
  if (!context) throw new Error('useStore must be used within a StoreProvider');
  return context;
}
